/**
 * themesAmQCM.js — QCM par thème du livret Thèmes du Permis AM.
 *
 * Un QCM de 3 questions pour chaque thème visible. Validé quand toutes
 * les bonnes réponses sont sélectionnées.
 *
 * Structure : { [slug]: { questions: [{ q, choices: [...], answer: 'A'|'B'|'C', explain? }] } }
 */

export const THEMES_QCM = {
  'signalisation': {
    questions: [
      {
        q: 'À quoi sert un panneau rond bordé de rouge ?',
        choices: [
          { key: 'A', label: 'Il donne une information touristique' },
          { key: 'B', label: 'Il indique une interdiction' },
          { key: 'C', label: 'Il signale un danger temporaire' },
        ],
        answer: 'B',
        explain: 'Les panneaux ronds à bordure rouge indiquent toujours une interdiction (sens interdit, limitation, stationnement interdit…).',
      },
      {
        q: 'Un panneau rond bleu indique :',
        choices: [
          { key: 'A', label: 'Une interdiction' },
          { key: 'B', label: 'Une obligation' },
          { key: 'C', label: 'Un simple conseil' },
        ],
        answer: 'B',
        explain: 'Les panneaux ronds bleus imposent une obligation (sens obligatoire, piste cyclable obligatoire…).',
      },
      {
        q: 'Un panneau triangulaire à bordure rouge signale :',
        choices: [
          { key: 'A', label: 'Une interdiction' },
          { key: 'B', label: 'Un danger à venir' },
          { key: 'C', label: 'La fin d\'une règle' },
        ],
        answer: 'B',
        explain: 'Les panneaux triangulaires bordés de rouge avertissent d\'un danger : virage, chaussée glissante, intersection…',
      },
    ],
  },

  'priorites': {
    questions: [
      {
        q: 'À un panneau STOP, je dois :',
        choices: [
          { key: 'A', label: 'Simplement ralentir' },
          { key: 'B', label: 'M\'arrêter complètement, même si la route est libre' },
          { key: 'C', label: 'Passer rapidement si personne n\'arrive' },
        ],
        answer: 'B',
        explain: 'Le STOP impose un arrêt total, roues immobiles, avant la ligne. Ensuite j\'observe et je repars quand la voie est libre.',
      },
      {
        q: 'À une intersection sans panneau ni marquage, la priorité est :',
        choices: [
          { key: 'A', label: 'À gauche' },
          { key: 'B', label: 'À droite' },
          { key: 'C', label: 'Au véhicule le plus rapide' },
        ],
        answer: 'B',
        explain: 'La priorité à droite s\'applique par défaut à toute intersection non signalée.',
      },
      {
        q: 'Un feu tricolore rouge me demande de :',
        choices: [
          { key: 'A', label: 'Ralentir et passer prudemment' },
          { key: 'B', label: 'M\'arrêter avant le feu' },
          { key: 'C', label: 'Continuer si aucun véhicule n\'arrive' },
        ],
        answer: 'B',
        explain: 'Un feu rouge est un ordre d\'arrêt absolu. Je m\'arrête avant la ligne, même si la voie semble libre.',
      },
    ],
  },

  'distances-securite': {
    questions: [
      {
        q: 'Quelle règle simple j\'utilise pour ma distance de sécurité ?',
        choices: [
          { key: 'A', label: 'La règle des 1 seconde' },
          { key: 'B', label: 'La règle des 2 secondes' },
          { key: 'C', label: 'La règle des 5 secondes' },
        ],
        answer: 'B',
        explain: 'Je repère un point fixe, je laisse passer le véhicule devant moi et je compte « un-mille, deux-mille » avant d\'y arriver.',
      },
      {
        q: 'Sous la pluie, ma distance de sécurité doit :',
        choices: [
          { key: 'A', label: 'Diminuer pour ne pas gêner' },
          { key: 'B', label: 'Rester identique' },
          { key: 'C', label: 'Augmenter car la chaussée est glissante' },
        ],
        answer: 'C',
        explain: 'La pluie double parfois la distance de freinage. J\'augmente ma marge pour compenser.',
      },
      {
        q: 'Plus je roule vite, ma distance de freinage :',
        choices: [
          { key: 'A', label: 'Diminue' },
          { key: 'B', label: 'Reste constante' },
          { key: 'C', label: 'Augmente fortement' },
        ],
        answer: 'C',
        explain: 'À 45 km/h, il faut plusieurs mètres pour s\'arrêter. Plus la vitesse monte, plus la distance grandit vite (au carré).',
      },
    ],
  },

  'virages': {
    questions: [
      {
        q: 'Dans un virage, mon regard doit :',
        choices: [
          { key: 'A', label: 'Rester fixé sur le capot' },
          { key: 'B', label: 'Chercher la sortie du virage' },
          { key: 'C', label: 'Regarder le bord de la route' },
        ],
        answer: 'B',
        explain: 'Le regard guide la trajectoire. En regardant vers la sortie, la voiturette suit naturellement le bon tracé.',
      },
      {
        q: 'Avant d\'aborder un virage, je :',
        choices: [
          { key: 'A', label: 'Accélère pour bien passer' },
          { key: 'B', label: 'Ralentis avant, pas dans le virage' },
          { key: 'C', label: 'Garde la même vitesse' },
        ],
        answer: 'B',
        explain: 'On freine AVANT le virage, on tourne, on relance à la sortie. Freiner dans le virage déstabilise le véhicule.',
      },
      {
        q: 'Une correction brutale de trajectoire dans un virage :',
        choices: [
          { key: 'A', label: 'Est sans conséquence' },
          { key: 'B', label: 'Peut faire perdre le contrôle' },
          { key: 'C', label: 'Améliore la stabilité' },
        ],
        answer: 'B',
        explain: 'Les corrections brusques réduisent l\'adhérence des pneus. Mieux vaut anticiper et rester souple sur les commandes.',
      },
    ],
  },

  'changements-direction': {
    questions: [
      {
        q: 'Quel est l\'ordre correct pour un changement de direction ?',
        choices: [
          { key: 'A', label: 'Clignotant → contrôle → action' },
          { key: 'B', label: 'Contrôle rétros → clignotant → contrôle → action' },
          { key: 'C', label: 'Action puis clignotant' },
        ],
        answer: 'B',
        explain: 'Je vérifie mes rétros, j\'annonce mon intention, je re-contrôle (angle mort), puis j\'agis.',
      },
      {
        q: 'Le clignotant sert à :',
        choices: [
          { key: 'A', label: 'Me donner automatiquement la priorité' },
          { key: 'B', label: 'Indiquer mon intention aux autres' },
          { key: 'C', label: 'Remplacer le contrôle visuel' },
        ],
        answer: 'B',
        explain: 'Le clignotant communique mon intention. Il ne me donne aucune priorité et ne remplace jamais l\'observation.',
      },
      {
        q: 'Après avoir changé de direction, je pense à :',
        choices: [
          { key: 'A', label: 'Laisser le clignotant allumé' },
          { key: 'B', label: 'L\'éteindre s\'il ne s\'est pas coupé' },
          { key: 'C', label: 'Klaxonner' },
        ],
        answer: 'B',
        explain: 'Un clignotant qui reste actif après le virage envoie une mauvaise information aux autres usagers.',
      },
    ],
  },

  'changements-direction-v2': null, // fallback

  'partage-route': {
    questions: [
      {
        q: 'Un piéton s\'approche d\'un passage protégé. Je :',
        choices: [
          { key: 'A', label: 'Klaxonne pour l\'avertir' },
          { key: 'B', label: 'Ralentis et le laisse traverser' },
          { key: 'C', label: 'Accélère pour passer avant lui' },
        ],
        answer: 'B',
        explain: 'Le piéton engagé ou manifestant l\'intention de traverser est prioritaire. Je ralentis et je m\'arrête si besoin.',
      },
      {
        q: 'Pour doubler un cycliste, je dois :',
        choices: [
          { key: 'A', label: 'Le doubler au plus près pour aller vite' },
          { key: 'B', label: 'Laisser une distance latérale d\'1 m minimum en ville' },
          { key: 'C', label: 'Klaxonner et passer sans écart' },
        ],
        answer: 'B',
        explain: 'Le Code de la route impose 1 m minimum en agglomération et 1,50 m hors agglomération lors du dépassement d\'un cycliste.',
      },
      {
        q: 'Un deux-roues motorisé est un usager :',
        choices: [
          { key: 'A', label: 'Toujours prévisible' },
          { key: 'B', label: 'Plus vulnérable qu\'une voiturette' },
          { key: 'C', label: 'Que je peux ignorer' },
        ],
        answer: 'B',
        explain: 'Les deux-roues motorisés sont plus vulnérables et parfois moins visibles. Je redouble d\'attention à leur approche.',
      },
    ],
  },

  'angles-morts': {
    questions: [
      {
        q: 'Un angle mort est :',
        choices: [
          { key: 'A', label: 'Une zone bien éclairée' },
          { key: 'B', label: 'Une zone que je ne vois pas dans mes rétros' },
          { key: 'C', label: 'Un point de repère utile' },
        ],
        answer: 'B',
        explain: 'L\'angle mort désigne une zone non visible par les rétroviseurs. Il faut tourner la tête pour la contrôler.',
      },
      {
        q: 'Près d\'un camion je dois :',
        choices: [
          { key: 'A', label: 'Rester dans ses angles morts' },
          { key: 'B', label: 'M\'en éloigner ou me rendre visible' },
          { key: 'C', label: 'Le doubler au plus vite' },
        ],
        answer: 'B',
        explain: 'Les camions ont d\'énormes angles morts. Si je ne vois pas le chauffeur dans son rétroviseur, il ne me voit pas.',
      },
      {
        q: 'Ne pas voir un autre usager signifie :',
        choices: [
          { key: 'A', label: 'Qu\'il n\'y a pas de danger' },
          { key: 'B', label: 'Qu\'il faut malgré tout anticiper sa présence' },
          { key: 'C', label: 'Que je peux m\'engager sans contrôle' },
        ],
        answer: 'B',
        explain: 'Absence de visibilité ≠ absence de danger. Je pars du principe qu\'un usager peut être caché.',
      },
    ],
  },

  'conditions-circulation': {
    questions: [
      {
        q: 'Sous la pluie j\'adapte principalement :',
        choices: [
          { key: 'A', label: 'La musique' },
          { key: 'B', label: 'Ma vitesse et mes distances de sécurité' },
          { key: 'C', label: 'Rien du tout' },
        ],
        answer: 'B',
        explain: 'La pluie réduit l\'adhérence et la visibilité. Je ralentis et j\'augmente ma marge.',
      },
      {
        q: 'En brouillard, j\'utilise :',
        choices: [
          { key: 'A', label: 'Uniquement mes feux de route' },
          { key: 'B', label: 'Mes feux de croisement et de brouillard si équipé' },
          { key: 'C', label: 'Aucun feu pour économiser la batterie' },
        ],
        answer: 'B',
        explain: 'Les feux de route sont interdits en brouillard (effet de mur blanc). Feux de croisement + feux de brouillard avant.',
      },
      {
        q: 'La nuit, je :',
        choices: [
          { key: 'A', label: 'Roule aussi vite que le jour' },
          { key: 'B', label: 'Adapte mon allure car la visibilité est réduite' },
          { key: 'C', label: 'Éteins mes feux pour économiser' },
        ],
        answer: 'B',
        explain: 'La nuit, la perception des distances change et les usagers vulnérables sont moins visibles. Je ralentis.',
      },
    ],
  },

  'entretien-vehicule': {
    questions: [
      {
        q: 'Des pneus sous-gonflés :',
        choices: [
          { key: 'A', label: 'Améliorent le freinage' },
          { key: 'B', label: 'Réduisent l\'adhérence et augmentent les distances de freinage' },
          { key: 'C', label: 'N\'ont aucun impact' },
        ],
        answer: 'B',
        explain: 'Un pneu sous-gonflé chauffe plus, s\'use plus vite et adhère moins bien. Je vérifie la pression régulièrement.',
      },
      {
        q: 'Les vérifications essentielles concernent :',
        choices: [
          { key: 'A', label: 'Uniquement les vitres' },
          { key: 'B', label: 'Pneus, freins, éclairage et niveaux' },
          { key: 'C', label: 'La couleur de la carrosserie' },
        ],
        answer: 'B',
        explain: 'La sécurité repose sur 4 grandes vérifications : pneumatiques, freinage, éclairage/signalisation, niveaux (liquide, batterie).',
      },
      {
        q: 'Un éclairage défectueux :',
        choices: [
          { key: 'A', label: 'N\'est problématique que la nuit' },
          { key: 'B', label: 'Est un défaut de sécurité, à corriger avant de rouler' },
          { key: 'C', label: 'Ne concerne que la police' },
        ],
        answer: 'B',
        explain: 'Un feu grillé rend le véhicule moins visible et empêche de signaler ses intentions. À réparer sans attendre.',
      },
    ],
  },

  'risques-comportements': {
    questions: [
      {
        q: 'Utiliser mon téléphone au volant :',
        choices: [
          { key: 'A', label: 'C\'est OK si c\'est rapide' },
          { key: 'B', label: 'C\'est une distraction majeure et un risque grave' },
          { key: 'C', label: 'C\'est autorisé avec un kit main-libre' },
        ],
        answer: 'B',
        explain: 'Le téléphone détourne le regard, les mains et l\'attention. Même bref, il crée un vrai risque et est interdit au volant.',
      },
      {
        q: 'Un ami me pousse à accélérer. Je :',
        choices: [
          { key: 'A', label: 'Accélère pour ne pas passer pour un peureux' },
          { key: 'B', label: 'Garde ma décision de sécurité' },
          { key: 'C', label: 'Suis toujours l\'avis du groupe' },
        ],
        answer: 'B',
        explain: 'Je conduis pour arriver en sécurité, pas pour impressionner. La pression des pairs ne remplace pas ma responsabilité.',
      },
      {
        q: 'La fatigue au volant :',
        choices: [
          { key: 'A', label: 'N\'impacte pas la conduite' },
          { key: 'B', label: 'Ralentit mes réflexes et ma capacité à observer' },
          { key: 'C', label: 'Se compense en accélérant' },
        ],
        answer: 'B',
        explain: 'La fatigue réduit toutes les capacités nécessaires à la conduite. Si je bâille ou que je décroche, je m\'arrête.',
      },
    ],
  },
};

/**
 * Vérifie si le QCM d'un thème est validé (toutes les bonnes réponses sélectionnées).
 * @param {string} slug — slug du thème
 * @param {object} answers — { [questionIdx]: 'A'|'B'|'C' }
 */
export function isQCMValidated(slug, answers) {
  const qcm = THEMES_QCM[slug];
  if (!qcm || !qcm.questions?.length) return false;
  return qcm.questions.every((q, i) => answers?.[i] === q.answer);
}

/** Compte les bonnes réponses. */
export function countCorrectAnswers(slug, answers) {
  const qcm = THEMES_QCM[slug];
  if (!qcm) return { correct: 0, total: 0 };
  const total = qcm.questions.length;
  const correct = qcm.questions.reduce((n, q, i) => n + (answers?.[i] === q.answer ? 1 : 0), 0);
  return { correct, total };
}

export default THEMES_QCM;
